// src/constants/invoiceColumns.ts
import React from 'react';
import { nanoid } from 'nanoid';
import { IntlShape } from 'react-intl';
import { UserModel } from '../../app/modules/auth/models/UserModel';
import { SettingMultiPipelines, Settings } from '../../interfaces/Settings';
import { CustomeStoreCompanyIdProps } from '../../stores/CustomeStore';
import { ColumnInvoices, ColumnType, InvoiceMultiPageSetting } from '../../interfaces/Invoices';
import { getInvoiceById, getInvoiceGeneratedById } from '../../api/InvoicesCRUD';
import { convertImageUrlToFile, convertToUTC, convertWhatsappToHtml } from '../../functions/general';

// ======================================================= Modal Reset Form =======================================================

export const handleModalClosed = (modalRef: React.RefObject<HTMLDivElement>, formik: any, setShowUpdateModal: React.Dispatch<React.SetStateAction<boolean>>) => {
  // Get the modal element
  const modalElement = modalRef.current

  // Define the event listener
  const handleModalHidden = () => {
    formik.resetForm()
    setShowUpdateModal(false)
  }

  // Attach the event listener
  modalElement?.addEventListener('hidden.bs.modal', handleModalHidden)

  // Cleanup
  return () => {
    modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden)
  }
}
// ======================================================= Set Variables Options =======================================================

export const setInvoiceVariables = (varType: 'all' | 'pipeline' | 'pipeline-multi-product' | 'pipeline-cf' | 'pipeline-multi-cf' | 'termin-cf' | 'merchant-cf', intl: IntlShape, user: UserModel, settings: Settings, companyId: CustomeStoreCompanyIdProps): ColumnInvoices[] => {

  const stringVariables = [
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage(
        { id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' },
        { title: settings.merchant_title }
      ),
      type: 'string',
      format: {
        source: 'leadClientName',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label:
        user.data.company_id === companyId.elgisa
          ? 'Opportunity'
          : intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
      type: 'string',
      format: {
        source: 'leadNote',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
      type: 'string',
      format: {
        source: 'leadPIC',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
      type: 'string',
      format: {
        source: 'leadProgressName',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
      type: 'string',
      format: {
        source: 'leadClientPhone',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
      type: 'string',
      format: {
        source: 'leadSourceName',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.STRING_VAR.PER_NAME' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'string',
      format: {
        source: 'leadProductName',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.STRING_VAR.PER_LABEL' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'string',
      format: {
        source: 'leadProductLabelname',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.STRING_VAR.PER_NOTE' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'string',
      format: {
        source: 'leadProductNote',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.STRING_VAR.PER_UNIT' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'string',
      format: {
        source: 'leadProductUnitName',
      },
    },
    // { // TODO: ADD CF
    //   id: nanoid(6),
    //   model: 'pipeline-multi-product',
    //   label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TERMIN_CURRENT_PENDING_CUSTOM_FIELD' }),
    //   type: 'string',
    //   format: {
    //     source: 'leadTerminCurrentPendingCustomField', // Termin Current Pending Custom Field
    //   },
    // },
    // { // TODO: ADD CF
    //   id: nanoid(6),
    //   model: 'pipeline-multi-product',
    //   label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CLIENT_CUSTOM_FIELD' }),
    //   type: 'string',
    //   format: {
    //     source: 'leadClientCustomField', // Custom Field Pipeline
    //   },
    // },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'FORM.LABEL.NAME' }) + ' ' + settings.pipeline_title,
      type: 'string',
      format: {
        source: 'leadTemplateName',
      },
    },
  ]

  const currencyVariables = [
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_VAT' }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      type: 'currency',
      format: {
        source: 'leadNominal',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_BEFORE_VAT' }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      type: 'currency',
      format: {
        source: 'leadNominalWithoutVat',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.TOTAL_VAT' }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      type: 'currency',
      format: {
        source: 'leadTotalVat',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.PER_PRICE' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      reference: 'products',
      type: 'currency',
      format: {
        source: 'leadProductPrice',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.PER_PRICE_WITH_VAT' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      reference: 'products',
      type: 'currency',
      format: {
        source: 'leadProductPriceWithVat',
      },
    },
    // {// TODO: AWASI
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.DISCOUNT_WITH_TITLE'}, {title: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT'})}),
    //     reference: 'products',
    //     type: 'currency',
    //     format: {
    //       source: 'leadProductDiscount',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.PER_HPP' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      reference: 'products',
      type: 'currency',
      format: {
        source: 'leadProductHpp',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.PER_TOTAL_PRICE' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      reference: 'products',
      type: 'currency',
      format: {
        source: 'leadProductTotalPrice',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.PER_TOTAL_EX_VAT' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }), // + ` (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
      reference: 'products',
      type: 'currency',
      format: {
        source: 'leadProductTotalPriceWithoutVat',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.LEAD_PRODUCT_DISCOUNT_NOMINAL' }),
      reference: 'products',
      type: 'currency',
      format: {
        source: 'leadProductDiscount',
      },
    },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'FORM.ACTION.PENDING_TERMIN'}, {title: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_VAT'})}) + ` per ${intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})} (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
    //     type: 'currency',
    //     format: {
    //       source: 'leadTerminCurrentPendingNominal',
    //     },
    // },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'FORM.ACTION.PENDING_TERMIN'}, {title: intl.formatMessage({id: 'FORM.LABEL.VAT'})}) + ` per ${intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})} (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`, 
    //     type: 'currency',
    //     format: {
    //       source: 'leadTerminCurrentPendingVat',
    //     },
    // },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'FORM.ACTION.PENDING_TERMIN'}, {title: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_BEFORE_VAT'})}) + ` per ${intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})} (${intl.formatMessage({id:'FORM.LABEL.CURRENCY'})})`,
    //     type: 'currency',
    //     format: {
    //       source: 'leadTerminCurrentPendingNominalWithoutVat',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.REMAINING_NOMINAL' }),
      type: 'currency',
      format: {
        source: 'leadTerminRemainingNominal',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.REMAINING_NOMINAL_WITHOUT_DP' }),
      type: 'currency',
      format: {
        source: 'leadTerminRemainingNominalWithoutDP',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.LEAD_DP_NOMINAL' }),
      type: 'currency',
      format: {
        source: 'leadTerminDPNominal',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'FORM.LABEL.LEAD_PRODUCT_VAT_NOMINAL' }), // Product Vat Nominal
      type: 'currency',
      format: {
        source: 'leadProductVatNominal',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      reference: 'products',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.LEAD_PRODUCT_TOTAL_DISCOUNT' }),
      type: 'currency',
      format: {
        source: 'leadTotalDiscount',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      reference: 'products',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.LEAD_PRODUCT_TOTAL_BEFORE_DISCOUNT' }),
      type: 'currency',
      format: {
        source: 'leadTotalNominalWithoutDiscount',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      reference: 'products',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.LEAD_PRODUCT_TOTAL_BEFORE_DISCOUNT_WITH_VAT' }),
      type: 'currency',
      format: {
        source: 'leadTotalNominalWithoutDiscountWithVat',
      },
    },
  ]

  const numberVariables = [
    // {
    //     id: nanoid(6),
    //     model: 'pipeline',
    //     label: intl.formatMessage({id: 'INVOICE.NUMBER_VAR.INCREMENT'}, {title: intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})}),
    //     type: 'number',
    //     format: {
    //       source: 'increment',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL_LOGS' }),
      type: 'number',
      format: {
        source: 'leadTotalLog',
      },
    },
    // { // TODO: HIDE: bentrok dengan currency
    //     id: nanoid(6),
    //     model: 'pipeline',
    //     label: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL'}) ,
    //     type: 'number',
    //     format: {
    //       source: 'leadNominal',
    //     },
    // },
    // {
    //   id: nanoid(6),
    //   model: 'pipeline',
    //   label: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_BEFORE_VAT'}),
    //   type: 'number',
    //   format: {
    //     source: 'leadNominalWithoutVat',
    //   },
    // },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline',
    //     label: intl.formatMessage({id: 'INVOICE.CURRENCY_VAR.TOTAL_VAT'}),
    //     type: 'number',
    //     format: {
    //       source: 'leadTotalVat',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.TOTAL_TYPES_PRODUCT' }),
      type: 'number',
      format: {
        source: 'leadSumProducts',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
      type: 'number',
      format: {
        source: 'leadAge',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.PER_QTY' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'number',
      format: {
        source: 'leadProductQty',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.CURRENCY_VAR.TOTAL_QTY' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'number',
      format: {
        source: 'leadTotalProductsQty',
      },
    },
    // { // TODO: HIDE: bentrok dengan currency
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'INVOICE.CURRENCY_VAR.PER_PRICE'}, {title: intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})}),
    //     reference: 'products',
    //     type: 'number',
    //     format: {
    //       source: 'leadProductPrice',
    //     },
    // },
    // { // TODO: HIDE
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.PRICE'}) + ' Discount',
    //     reference: 'products',
    //     type: 'number',
    //     format: {
    //       source: 'leadProductDiscount',
    //     },
    // },
    // { // TODO: HIDE: bentrok dengan currency
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'INVOICE.CURRENCY_VAR.PER_HPP'}, {title: intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})}),
    //     reference: 'products',
    //     type: 'number',
    //     format: {
    //       source: 'leadProductHpp',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.PER_UNIT_CONVERT' }, { title: intl.formatMessage({ id: 'FORM.MENU.PRODUCTS' }) }),
      reference: 'products',
      type: 'number',
      format: {
        source: 'leadProductUnitConvertionToPieces',
      },
    },
    // { // TODO: HIDE: bentrok dengan currency
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'INVOICE.CURRENCY_VAR.PER_TOTAL_PRICE'}, {title: intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})}),
    //     reference: 'products',
    //     type: 'number',
    //     format: {
    //       source: 'leadProductTotalPrice',
    //     },
    // },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline-multi-product',
    //     label: intl.formatMessage({id: 'INVOICE.CURRENCY_VAR.PER_TOTAL_EX_VAT'}, {title: intl.formatMessage({id: 'FORM.MENU.PRODUCTS'})}),
    //     reference: 'products',
    //     type: 'number',
    //     format: {
    //       source: 'leadProductTotalPriceWithoutVat',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'FORM.LABEL.TOTAL' }, { title: intl.formatMessage({ id: 'FORM.LABEL.TERMIN' }) }),
      type: 'number',
      format: {
        source: 'leadTerminTotalCount',
      },
    },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline',
    //     label: intl.formatMessage({id: 'FORM.ACTION.PENDING_TERMIN'}, {title: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_VAT'})}),
    //     type: 'number',
    //     format: {
    //       source: 'leadTerminCurrentPendingNominal',
    //     },
    // },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline',
    //     label: intl.formatMessage({id: 'FORM.ACTION.PENDING_TERMIN'}, {title: `(${intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.VAT'})})`}),
    //     type: 'number',
    //     format: {
    //       source: 'leadTerminCurrentPendingVat',
    //     },
    // },
    // {
    //     id: nanoid(6),
    //     model: 'pipeline',
    //     label: intl.formatMessage({id: 'FORM.ACTION.PENDING_TERMIN'}, {title: intl.formatMessage({id: 'FORM.LABEL.VAT'})}),
    //     type: 'number',
    //     format: {
    //       source: 'leadTerminCurrentPendingNominalWithoutVat',
    //     },
    // },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'FORM.ACTION.PENDING_TERMIN' }, { title: 'Index' }),
      type: 'number',
      format: {
        source: 'leadTerminCurrentPendingIndex',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.REMAINING_COUNT_TERM_WITHOUT_DP' }),
      type: 'number',
      format: {
        source: 'leadTerminRemainingCountWithoutDP',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      reference: 'products',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.LEAD_PRODUCT_DISCOUNT_PERCENTAGE' }),
      type: 'number',
      format: {
        source: 'leadProductDiscountPercentage',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline-multi-product',
      reference: 'products',
      label: intl.formatMessage({ id: 'INVOICE.NUMBER_VAR.LEAD_PRODUCT_VAT_PERCENTAGE' }),
      type: 'number',
      format: {
        source: 'leadProductVatPercentage',
      },
    },
  ]

  const timestampVariable = [
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_CREATED' }),
      type: 'timestamp',
      format: {
        source: 'leadDateCreated',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
      type: 'timestamp',
      format: {
        source: 'leadDateStarted',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
      type: 'timestamp',
      format: {
        source: 'leadDateEnded',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'MASTERS.DATATABLE.EXPANDED.INVOICE_DUE_DATE' }),
      type: 'timestamp',
      format: {
        source: 'leadTerminCurrentPendingDueDate',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'FORM.LABEL.CURRENT_TIMESTAMP' }),
      type: 'timestamp',
      format: {
        source: 'currentTimestamp',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'FORM.LABEL.DUE_DATE_DP_TERMIN' }), // Jatuh Tempo DP Termin
      type: 'timestamp',
      format: {
        source: 'leadTerminDPDueDate',
      },
    },
    {
      id: nanoid(6),
      model: 'pipeline',
      label: intl.formatMessage({ id: 'FORM.LABEL.DATE_PAYMENT_TERMIN' }), // Jatuh Tempo DP Termin
      type: 'timestamp',
      format: {
        source: 'leadTerminCurrentPaidDate',
      },
    },
  ];

  const singleVariable = [...stringVariables, ...numberVariables, ...currencyVariables, ...timestampVariable].filter((v) => v.model === 'pipeline');
  const multiVariable = [...stringVariables, ...currencyVariables, ...numberVariables, ...timestampVariable];

  return varType === 'all' ? multiVariable as ColumnInvoices[] : singleVariable as ColumnInvoices[];

};
// ======================================================= Fetch Selected MultiPipeline =======================================================

// export const getSelectedAccessProgressPipeline = async (templateId: string, token: string, setPipeProgress: React.Dispatch<React.SetStateAction<SettingMultiPipelines[]>>) => {
//   try {
//     const response = await getAccessProgressesPipeline(templateId, token)
//     if (response.data.success) {
//       setPipeProgress(response.data.data.map((progressItem: any) => ({
//         ...progressItem,
//         label: progressItem.name,
//         value: progressItem.id,
//       })))
//     }
//   } catch (error) {
//     console.log('error', error)
//   }
// }
// ======================================================= Set Custom Field Variables Options =======================================================

export const setCustomFieldVariables = (intl: IntlShape, user: UserModel, settings: Settings, companyId: CustomeStoreCompanyIdProps, multiPipelines: SettingMultiPipelines[], formik: any, setColumn: React.Dispatch<React.SetStateAction<any[]>>) => {
  const typeString = ['text', 'option', 'link', 'phone', 'serial', 'custom-serial', 'email', 'paragraph']
  const typeArray = ['multiple', 'multipleOption'] //'merchant'
  const typeNumber = ['number']
  const typeTimestamp = ['date']
  const typeAttachment = ['image', 'images', 'file', 'files']
  // const typeCRM_Menu = ['pipeline', 'merchant', 'termin'] //'user',

  const mainColumn: ColumnInvoices[] = setInvoiceVariables('all', intl, user, settings, companyId)
  const index = multiPipelines.findIndex(
    (pipeline) => pipeline.id === formik.values.leadTemplateId
  )

  if (index !== -1) {
    const pipeline = multiPipelines[index]
    const customColumn = pipeline.customFields

    const pipelineCustomFieldColumn = Object.keys(customColumn).reduce((acc: any[], key: string) => {
      // console.log('accc', acc)

      if (
        !typeAttachment.includes(customColumn[key].type) &&
        (typeString.includes(customColumn[key].type) ||
          typeTimestamp.includes(customColumn[key].type) ||
          typeArray.includes(customColumn[key].type) ||
          typeNumber.includes(customColumn[key].type))
      ) {
        if (typeArray.includes(customColumn[key].type)) return acc

        acc.push({
          id: nanoid(6),
          label: customColumn[key].name as string,
          type: typeString.includes(customColumn[key].type)
            ? 'string'
            : typeTimestamp.includes(customColumn[key].type)
              ? 'timestamp'
              : // : typeAttachment.includes(customColumn[key].type)
              // ? 'attachment'
              typeArray.includes(customColumn[key].type)
                ? 'array'
                : 'number',
          format: {
            source: key,
          },
          reference: typeArray.includes(customColumn[key].type) ? 'customFields' : 'not_set', // TODO: (UNUSED) => only array to be transformed dnd_data -> list
          model: typeArray.includes(customColumn[key].type)
            ? 'pipeline-multi-cf'
            : 'pipeline-cf', // TODO: (UNUSED) => only array to be transformed dnd_data -> list
        })
      }

      return acc
    },
      []
    )

    const mergeColumn: any[] = [...mainColumn, ...pipelineCustomFieldColumn]
    setColumn(mergeColumn)
  }
};
// ======================================================= Fetch Detail Data Invoice =======================================================

export const getInvoiceDetail = async (
  context: 'update' | 'preview',
  invoice: InvoiceMultiPageSetting,
  token: string,
  formik: any,
  column: ColumnInvoices[],
  setMultiPages: (value: any) => void,
  setIsLoading: (value: boolean) => void
) => {
  try {
    const req = context === 'update' ? await getInvoiceById(token, invoice.id) : await getInvoiceGeneratedById(token, invoice.id)
    const res = req.data.data as InvoiceMultiPageSetting
    formik.setValues({
      id: res.id as string,
      progressId: invoice.progress!.id,
      name: res.name as string,
      // caption: res.caption as string,
      caption: convertWhatsappToHtml([res.caption as string], 'defaultValue', true),
      leadTemplateId: (res.leadTemplate?.id as string) ?? '',
      format: res.format,
      // backgroundImage: (await convertImageUrlToFile(res.templatePath, 'image.jpg')) as any,
      backgroundImage: [],
    })

    res.pages = await Promise.all(
      res.pages.map(async (page, index) => {
        if (page.templatePath && page.templatePath !== '') {
          const buildImageURL = (await convertImageUrlToFile(
            page.templatePath,
            `image-${page.name}.png`
          )) as any
          // console.log('buildImageURL', buildImageURL)

          const img = new Image();
          img.src = page.templatePath;

          return new Promise((resolve) => {
            img.onerror = () => {
              console.error("Failed to load image");
              resolve(page);
            };

            img.onload = () => {
              if (context === 'update') {
                const reader = new FileReader();
                reader.readAsDataURL(buildImageURL); // Konversi Blob ke Base64

                reader.onloadend = () => {
                  page.imageFile = {
                    id: nanoid(6),
                    name: `page-${index + 1}.${buildImageURL.type.split("/")[1]}`,
                    size: buildImageURL.size,
                    value: reader.result as string, // Base64 string
                    width: img.width,
                    height: img.height,
                  };
                  resolve(page); // Pastikan mengembalikan setelah base64 selesai

                };
              } else if (context === 'preview') {
                page.imageFile = {
                  id: nanoid(6),
                  name: `page-${index + 1}.${buildImageURL.type.split('/')[1]}`,
                  size: buildImageURL.size,
                  value: URL.createObjectURL(buildImageURL),
                  width: img.width,
                  height: img.height,
                }
                resolve(page); // Pastikan mengembalikan setelah base64 selesai
              }
            };
          });

        }
        return page
      })
    )

    const setVariablesContentId = (field: any) => {
      const getModelTypeDnD =
        Object.keys(field).find((key) => key !== 'id' && key !== 'name') ?? 'base'
      const parsedMessage = field[getModelTypeDnD]?.content?.flatMap((part: any) => {
        if (typeof part === 'string') {
          return part
        } else if (typeof part === 'object') {
          return Object.keys(part).map((key) => {
            return {
              [key]: {
                ...part[key],
                id: nanoid(6),
              },
            }
          })
        }
        return []
      })

      return parsedMessage
    }

    const parsedVariables = (field: any): ColumnInvoices[] => {
      const getModelTypeDnD = Object.keys(field).find((key) => key !== 'id' && key !== 'name') ?? 'base'

      return field[getModelTypeDnD].content.flatMap((part: any) => {
        if (typeof part === 'object') {
          const result: ColumnInvoices[] = []

          for (const parentKey in part) {
            // let varType = parentKey as ColumnType
            for (const key in part[parentKey]) {
              if (key === 'id') {
                if (part[parentKey].source === 'leadCustomField' && part[parentKey].key) {
                  // if (column.find((col) => col.format.source === part.key)?.label === undefined)
                  //   return []
                  result.push({
                    id: part[parentKey].id,
                    label:
                      column.find((col) => col.format?.source === part[parentKey].key)?.label ||
                      '@???',
                    type: parentKey as ColumnType,
                    defaultValue:
                      parentKey === "timestamp"
                        ? convertToUTC(part[parentKey].defaultValue, part[parentKey].timezone)
                        : part[parentKey].defaultValue,
                    // part[parentKey].defaultValue,
                    format: {
                      ...part[parentKey],
                      source:
                        part[parentKey].source === 'leadCustomField'
                          ? part[parentKey].key
                          : part[parentKey].source,
                    },
                  })
                } else {
                  result.push({
                    id: part[parentKey].id,
                    label:
                      column.find((col) => col.format?.source === part[parentKey].source)?.label ||
                      "#???",
                    type: parentKey as ColumnType,
                    defaultValue:
                      parentKey === "timestamp"
                        ? convertToUTC(part[parentKey].defaultValue, part[parentKey].timezone)
                        : part[parentKey].defaultValue,
                    format: {
                      ...part[parentKey],
                      ...(parentKey === "currency" ? { currencyType: "number" } : parentKey === "currencyString" ? { currencyType: "string" } : {}),
                    },
                  });
                }
              }
            }
          }
          return result
        }
        return []
      })
    }

    res.pages.map((page, index) => {
      page.fields.map((field: any) => {
        const getModelTypeDnD =
          Object.keys(field).find((key) => key !== 'id' && key !== 'name') ?? 'base'
        field[getModelTypeDnD] = {
          ...field[getModelTypeDnD],
          content: setVariablesContentId(field),
        }
        return field
      })

      page.fields.map((field: any) => {
        const getModelTypeDnD =
          Object.keys(field).find((key) => key !== 'id' && key !== 'name') ?? 'base'
        field[getModelTypeDnD] = {
          ...field[getModelTypeDnD],
          formattedContent: field[getModelTypeDnD].content,
          // previewQtyHtml: `<p>${convertWhatsappToHtml(field[getModelTypeDnD].content)}</p>`,
          previewQtyHtml: `${convertWhatsappToHtml(field[getModelTypeDnD].content)}`,
        }
        return field
      })
      return page
    })

    const setVariables = res.pages.map((page, index) => {
      const setPageVariables = page.fields.map((field: any) => {
        const getModelTypeDnD =
          Object.keys(field).find((key) => key !== 'id' && key !== 'name') ?? 'base'
        return {
          ...field,
          dnd_data: {
            ...field[getModelTypeDnD],
            variables: parsedVariables(field),
          },
          model:
            field.list?.reference === 'products'
              ? 'pipeline-multi-product'
              : field.list?.reference === 'customFields'
                ? 'pipeline-multi-cf'
                : 'pipeline-cf',
        }
      })
      return {
        ...page,
        id: nanoid(6),
        fields: setPageVariables
      }
    })

    // menggantikan variable dengan placeholder nya
    // jika ada variable yang memiliki nilai default maka digantikan dengan placeholder nya
    // jika tidak maka biarkan saja
    setVariables.map((page: any, index) => {
      page.fields.map((field: any) => {
        let updatedFormattedContent = field.dnd_data.formattedContent
          .map((part: any) => {
            if (typeof part === 'string') {
              return part
            } else if (typeof part === 'object') {
              for (const key in part) {
                // if (key === 'timestamp')
                //   return moment(part[key].defaultValue).format(part[key]?.format);
                // console.log('keysss', key)
                if (part[key]?.source === 'leadCustomField') {
                  return '@[' + part[key].id + '∋' + part[key].key + ']'
                }
                if (part[key]?.defaultValue !== undefined && part[key]?.defaultValue !== null) {
                  // return '@['+part[key].id +'∋' +part[key].defaultValue+']';
                  return '@[' + part[key].id + '∋' + part[key].source + ']'
                }
              }
              return ''
            }
            return ''
          })
          .join('')

        field.dnd_data.variables.forEach((variable: ColumnInvoices) => {
          // const escapedDefaultValue = String(variable.defaultValue).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const escapedDefaultValue = String(variable.defaultValue).replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&'
          )
          // const regex = new RegExp(escapedDefaultValue, 'g')
          const regex = new RegExp(`@\\[${variable.id}∋${variable.format!.source}\\]`, 'g')

          updatedFormattedContent = updatedFormattedContent.replace(regex, `[${variable.label}_${variable.id}]`)

        })

        const renderMultipleProductHTML = () => { // preveiw mode only
          let previewQtyHtml = ''
          for (let i = 0; i < field.dnd_data.formattedContent.length; i++) {
            const parsedHtml = `<p style="margin-bottom:0px !important; line-height: 8px;">${field.dnd_data.formattedContent[i] ?? ''
              }</p>`
            // previewQtyHtml += `${parsedHtml}`
            previewQtyHtml += `<div style="margin-bottom: ${(field.dnd_data.spacing ?? '0') + 'px'}; ${i === 0 ? 'margin-top: 8px' : ''
              }">${parsedHtml}</div>`
          }
          return previewQtyHtml
        }
        if (context === 'update') {
          field.dnd_data = {
            ...field.dnd_data,
            formattedContent: `${updatedFormattedContent}`,
            previewQtyHtml: `${updatedFormattedContent}`,
          }
          return field
        } else if (context === 'preview') {
          field.dnd_data = {
            ...field.dnd_data,
            // formattedContent: updatedFormattedContent,
            formattedContent: `<p>${updatedFormattedContent}</p>`,
            // previewQtyHtml: `<p>${updatedFormattedContent}</p>`,
            previewQtyHtml: renderMultipleProductHTML(),
          }
          return field
        }
        return field
      })
      return page
    })

    setMultiPages(setVariables)
    setIsLoading(false)
  } catch (error) {
    console.error(error)
  }
}

// ======================================================= Download Invoice PDF =======================================================

export const downloadInvoicePDF = async (invoiceData: InvoiceMultiPageSetting, intl: IntlShape) => {
  try {
    // Untuk auto download, kita akan menggunakan approach yang lebih sederhana
    // menggunakan canvas dan jsPDF langsung tanpa DOM refs

    if (!invoiceData.pages || invoiceData.pages.length === 0) {
      throw new Error('No pages found in invoice data')
    }

    // Import jsPDF
    let jsPDF
    try {
      const jsPDFModule = await import('jspdf')
      jsPDF = jsPDFModule.jsPDF
    } catch (error) {
      console.error('Failed to import jsPDF:', error)
      throw new Error('PDF library not available')
    }

    let filename = invoiceData.name?.slice(0, 30) || `invoice_download_${new Date().getTime()}`

    // Create PDF with first page dimensions
    const firstPage = invoiceData.pages[0]
    let pdfWidth = 800
    let pdfHeight = 600

    // Try to get dimensions from templatePath if available
    if (firstPage.templatePath) {
      try {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        await new Promise((resolve, reject) => {
          img.onload = () => {
            pdfWidth = img.width
            pdfHeight = img.height
            resolve(true)
          }
          img.onerror = () => reject(new Error('Failed to load image'))
          img.src = firstPage.templatePath!
        })
      } catch (error) {
        console.warn('Could not load template image, using default dimensions')
      }
    }

    // Create PDF
    const pdf = new jsPDF({
      orientation: pdfWidth > pdfHeight ? 'l' : 'p',
      unit: 'px',
      format: [pdfWidth, pdfHeight]
    })

    // Add pages to PDF
    for (let i = 0; i < invoiceData.pages.length; i++) {
      const page = invoiceData.pages[i]

      if (i > 0) {
        pdf.addPage([pdfWidth, pdfHeight])
      }

      // Add background image if available
      if (page.templatePath) {
        try {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          await new Promise((resolve, reject) => {
            img.onload = () => resolve(true)
            img.onerror = () => reject(new Error('Failed to load page image'))
            img.src = page.templatePath!
          })

          // Add image to PDF
          pdf.addImage(page.templatePath, 'PNG', 0, 0, pdfWidth, pdfHeight)
        } catch (error) {
          console.warn(`Could not load image for page ${i + 1}:`, error)
        }
      }

      // Add text fields
      if (page.fields && page.fields.length > 0) {
        page.fields.forEach(field => {
          if (field.base && field.base.content && field.base.position) {
            const fontSize = field.base.font?.size || 12
            const fontName = field.base.font?.name || 'helvetica'

            pdf.setFontSize(fontSize)
            pdf.setFont(fontName.toLowerCase())

            // Get text content
            let textContent = ''
            if (Array.isArray(field.base.content)) {
              textContent = field.base.content.join(' ')
            } else if (typeof field.base.content === 'string') {
              textContent = field.base.content
            }

            // Remove HTML tags if present
            textContent = textContent.replace(/<[^>]*>/g, '')

            // Add text to PDF
            if (textContent.trim()) {
              pdf.text(textContent, field.base.position.x, field.base.position.y)
            }
          }
        })
      }
    }

    // Download the PDF
    pdf.save(`${filename}-original.pdf`)

    return {
      success: true,
      message: 'PDF downloaded successfully'
    }
  } catch (error) {
    console.error('Error downloading PDF:', error)
    return {
      success: false,
      message: 'Failed to download PDF',
      error: error
    }
  }
}